<template>
    <section-wrapper
        v-if="investmentStrategyRecommendedBasedOnRiskTolerance"
        :class="Section.RISK_TOLERANCE_EVALUATION.cssClassName"
        :this-section="Section.RISK_TOLERANCE_EVALUATION"
        :next-section="checkRiskForNextSection"
        :section-end-button-label="$t('riskToleranceEvaluation.sectionEndButtonLabel.default')"
        :section-end-button-in-dom="!acceptedOption || !hasIncreasedRisk || isAcceptRiskSelected"
        :section-end-button-is-disabled="!acceptedOption && hasIncreasedRisk">
        <div
            class="row">
            <div class="col">
                <h2>{{ $t('riskToleranceEvaluation.title') }}</h2>

                <div class="row">
                    <div class="col">
                        <p class="risk-tolerance-evaluation__summary">
                            {{ $t(`riskToleranceEvaluation.summary`) }}
                        </p>

                        <!-- success box -->
                        <div
                            v-if="!hasIncreasedRisk"
                            class="risk-tolerance-evaluation__box d-flex flex-row">
                            <Icon
                                class="risk-tolerance-evaluation__icon"
                                :type="'check'"
                                :width="iconSize"
                                :height="iconSize" />

                            <div
                                v-if="!viewportNoMatch">
                                <h3>{{ $t(`investmentStrategyType.${investmentStrategySelected.name}.info.title`) }}</h3>

                                <p>
                                    {{ $t('riskToleranceEvaluation.boxText.equalRisk',
                                          { recommendedInvestmentStrategyDescription: $t(`riskToleranceEvaluation.riskDescription.${recommendedStrategyName}`),
                                            investmentStrategySelectedDescription: $t(`riskToleranceEvaluation.riskDescription.${investmentStrategySelected.name}`)
                                          }) }}
                                </p>
                            </div>

                            <h3
                                class="risk-tolerance-evaluation__title--mobile"
                                v-if="viewportNoMatch">
                                {{ $t(`investmentStrategyType.${investmentStrategySelected.name}.info.title`) }}
                            </h3>

                            <p
                                v-if="viewportNoMatch">
                                {{ $t('riskToleranceEvaluation.boxText.equalRisk',
                                      { recommendedInvestmentStrategyDescription: $t(`riskToleranceEvaluation.riskDescription.${recommendedStrategyName}`),
                                        investmentStrategySelectedDescription: $t(`riskToleranceEvaluation.riskDescription.${investmentStrategySelected.name}`)
                                      }) }}
                            </p>
                        </div>

                        <!-- decision box -->
                        <div
                            v-if="hasIncreasedRisk"
                            class="risk-tolerance-evaluation__box">
                            <!-- content !-->
                            <div
                                class="risk-tolerance-evaluation__content">
                                <h3>{{ $t(`investmentStrategyType.${recommendedStrategyName}.info.title`) }}</h3>

                                <p>
                                    {{ $t('riskToleranceEvaluation.boxText.increasedRisk',
                                          { recommendedInvestmentStrategyDescription: $t(`riskToleranceEvaluation.riskDescription.${recommendedStrategyName}`),
                                            investmentStrategySelectedDescription: $t(`riskToleranceEvaluation.riskDescription.${investmentStrategySelected.name}`),
                                            recommendedInvestmentStrategy: $t(`investmentStrategyType.${recommendedStrategyName}.title`)
                                          }) }}
                                </p>
                            </div>

                            <!-- checkboxes -->
                            <div>
                                <button
                                    class="btn btn-secondary risk-tolerance-evaluation__checkbox-button"
                                    :disabled="!isCurrentSection"
                                    @click="acceptedOption = RiskToleranceEvaluationOption.ACCEPT_RECOMMENDATION">
                                    <Icon
                                        class="radio-item__radio"
                                        :type="acceptedOption === RiskToleranceEvaluationOption.ACCEPT_RECOMMENDATION ? 'radio-square-checked' : 'radio-square'"
                                        :width="25"
                                        :height="25" />
                                    {{ $t('riskToleranceEvaluation.checkbox.acceptRecommendation') }}
                                </button>

                                <button
                                    class="btn btn-evaluation-accept risk-tolerance-evaluation__checkbox-button"
                                    :disabled="!isCurrentSection"
                                    @click="acceptedOption = RiskToleranceEvaluationOption.ACCEPT_RISK">
                                    <Icon
                                        class="radio-item__radio"
                                        :type="acceptedOption === RiskToleranceEvaluationOption.ACCEPT_RISK ? 'radio-square-checked' : 'radio-square'"
                                        :width="25"
                                        :height="25" />
                                    {{ $t('riskToleranceEvaluation.checkbox.acceptRisk', { investmentStrategySelected:
                                        $t(`investmentStrategyType.${investmentStrategySelected.name}.title`) }) }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="isAcceptRecommendationSelected"
                    class="row">
                    <div class="col">
                        <investment-strategy-change-notification
                            tense="future" />
                    </div>
                </div>
            </div>
        </div>

        <div
            class="row"
            v-if="isAcceptRecommendationSelected">
            <div class="col text-right">
                <button
                    class="btn btn-primary risk-tolerance-evaluation__section-end-button"
                    @click="handleConfirmAcceptRecommendation">
                    <Icon
                        type="button-arrow"
                        :height="16"
                        :width="9" />
                    {{ $t('riskToleranceEvaluation.sectionEndButtonLabel.acceptRecommendation') }}
                </button>
            </div>
        </div>
    </section-wrapper>
</template>

<script>

import { mapActions, mapState, mapGetters } from 'vuex';
import { scrollToExistingElement } from '@/services/scrollToService';
import SectionWrapper from '@/components/common/SectionWrapper';
import { Section, RiskToleranceEvaluationOption } from '@/enums';
import InvestmentStrategyChangeNotification from '@/components/InvestmentStrategyChangeNotification';

export default {
    name: 'RiskToleranceEvaluation',
    components: {
        SectionWrapper,
        InvestmentStrategyChangeNotification,
    },
    computed: {
        ...mapState(['openSections']),
        ...mapGetters(['investmentStrategySelected', 'investmentStrategyRecommendedBasedOnRiskTolerance', 'viewportNoMatch']),
        recommendedStrategyName () {
            return this.investmentStrategyRecommendedBasedOnRiskTolerance.name;
        },
        hasIncreasedRisk () {
            return this.investmentStrategySelected.risk > this.investmentStrategyRecommendedBasedOnRiskTolerance.risk;
        },
        isCurrentSection () {
            return this.openSections[this.openSections.length - 1] === Section.RISK_TOLERANCE_EVALUATION;
        },
        isAcceptRiskSelected () {
            return (this.hasIncreasedRisk && this.acceptedOption === RiskToleranceEvaluationOption.ACCEPT_RISK);
        },
        isAcceptRecommendationSelected () {
            return (this.hasIncreasedRisk && this.acceptedOption === RiskToleranceEvaluationOption.ACCEPT_RECOMMENDATION);
        },
        iconSize () {
            return (this.viewportNoMatch ? 48 : 88);
        },
        checkRiskForNextSection () {
            if (this.isAcceptRiskSelected) {
                this.updateRiskToleranceWithSelectedStrategy();
            }
            return this.isAcceptRecommendationSelected ? null : Section.THEME;
        },
    },
    methods: {
        ...mapActions(['setInvestmentStrategySelected', 'updateRiskToleranceWithSelectedStrategy']),
        handleConfirmAcceptRecommendation () {
            scrollToExistingElement('.investment-strategy');
            // timeout needed to avoid scrolling bug in Firefox
            setTimeout(() => {
                this.setInvestmentStrategySelected(this.investmentStrategyRecommendedBasedOnRiskTolerance);
            }, 500);
        },
    },
    data () {
        return {
            Section,
            RiskToleranceEvaluationOption,
            acceptedOption: null,
        };
    },
};
</script>

<style scoped lang="scss">
.risk-tolerance-evaluation {
  &__summary {
    margin-bottom: 30px;
    @include media-breakpoint-down(xs) {
      margin-bottom: 15px;
    }
  }

  &__icon {
    margin: 30px 60px 45px 45px;

    @include media-breakpoint-down(xs) {
      margin: 10px 22px 10px 22px;
    }
  }

  &__box {
    padding: 30px 15px;
    background-color: $mb-lightgreen;
    color: $white;
    @include media-breakpoint-down(xs) {
      padding: 15px 8px;
      flex-wrap: wrap;
    }

    .btn-evaluation-accept {
      background-color: $mb-grey6;
    }

    p {
      margin-bottom: 20px;
      color: $white;
      @include media-breakpoint-down(xs) {
        margin-bottom: 15px;
      }
    }

    h3 {
      margin-bottom: 20px;
      font-size: 26px;
      line-height: 28px;
      color: $white;
      @include media-breakpoint-down(xs) {
        margin-bottom: 15px;
        font-size: 20px;
        line-height: 24px;
      }
    }
  }

  &__checkbox-button {
    padding: 7px 15px;
    @include font-migrosbank();
    @include media-breakpoint-up(lg) {
      width: calc(50% - 7.5px);
    }

    @include media-breakpoint-down(xs) {
      display: block;
    }

    + .risk-tolerance-evaluation__checkbox-button {
      @include media-breakpoint-up(lg) {
        margin-left: 15px;
      }

      @include media-breakpoint-down(xs) {
        margin-top: 5px;
      }
    }
  }

  &__section-end-button {
    margin-top: 20px;

      @include media-breakpoint-down(xs) {
          margin-top: 15px;
      }
  }
}

.risk-tolerance-evaluation__title--mobile {
  width: 60%;
}
</style>
